// Light or dark theme. With no saved choice the page follows the system setting (CSS media query);
// a choice made with the theme button is saved in this browser only and wins over the system.

import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const KEY = 'diagnuvo.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** The theme in use: a saved choice, else the system setting. Anything else saved is ignored. */
export function pickTheme(saved: string | null, systemDark: boolean): Theme {
  if (saved === 'light' || saved === 'dark') return saved;
  return systemDark ? 'dark' : 'light';
}

function savedTheme(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null; // storage blocked: follow the system
  }
}

/** Sets a saved choice on the page before the first render. */
export function applySavedTheme(): void {
  const saved = savedTheme();
  if (saved === 'light' || saved === 'dark') document.documentElement.dataset.theme = saved;
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState(() =>
    pickTheme(savedTheme(), window.matchMedia(DARK_QUERY).matches),
  );
  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    const follow = (e: MediaQueryListEvent) => setTheme(pickTheme(savedTheme(), e.matches));
    media.addEventListener('change', follow);
    return () => media.removeEventListener('change', follow);
  }, []);
  const toggle = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // not saved: the choice lasts until the page reloads
    }
    setTheme(next);
  };
  return [theme, toggle];
}
